import { useTranslation } from 'react-i18next';
import { useJobsQuery } from '@/hooks/useJobs';
import type { JobName } from '@/api/types';
import { JobCard } from './JobCard';
import { UserSourceCard } from './UserSourceCard';
import { AdSyncCard } from './AdSyncCard';
import { OuSourceCard } from './OuSourceCard';
import { DomainsCard } from './DomainsCard';
import { SentinelsCard } from './SentinelsCard';
import { PadronCard } from './PadronCard';
import { DelegationCard } from './DelegationCard';

/**
 * Las tres pestañas. Se declara AQUÍ, junto al reparto de corridas, para que no puedan divergir: el
 * `Record` de abajo obliga a que cada pestaña declarada tenga las suyas.
 */
export type JobsSubTab = 'usuarios' | 'ous' | 'dominios' | 'centinelas' | 'aNombreDe';

/**
 * Qué corridas pertenecen a cada pestaña.
 *
 * 🔴 El reparto es exhaustivo y sin solapes: los cinco jobs tienen exactamente un sitio. Eso importa
 * porque una corrida que no aparezca en ninguna pestaña es una corrida que nadie va a mirar — y la
 * prueba `cada corrida del backend pertenece a exactamente UNA pestaña` lo fija, para que añadir un
 * job nuevo al backend no lo deje invisible en silencio.
 */
export const JOBS_POR_PESTANA: Record<JobsSubTab, readonly JobName[]> = {
  usuarios: ['UserCatalogSync', 'SuccessFactorsUserSync'],
  ous: ['OuCatalogSync', 'SuccessFactorsOuSync'],
  dominios: ['AdDomainSweep'],
  // 🔴 Sin corridas propias, y a propósito: los centinelas los consume el sync de SuccessFactors, que
  // vive en la pestaña de Usuarios. Dejar la lista vacía —en vez de duplicar ahí esa corrida— mantiene
  // la invariante de que cada job aparece en EXACTAMENTE una pestaña, que es lo que fija la prueba.
  centinelas: [],
  // Tampoco tiene corridas: la regla la leen la caja «A nombre de» y la carga masiva, no un job.
  aNombreDe: [],
};

/** El histórico de las corridas de una pestaña: las tarjetas que van DEBAJO de su configuración. */
function CorridasDe({ pestana }: { pestana: JobsSubTab }) {
  const { t } = useTranslation();
  const { data: jobs } = useJobsQuery();
  const deEstaPestana = (jobs ?? []).filter(job => JOBS_POR_PESTANA[pestana].includes(job.jobName));

  return (
    <>
      <h3 className="jobs-section-title">{t('jobs.runsTitle')}</h3>
      {deEstaPestana.length === 0
        ? <p className="jobs-card-meta">{t('jobs.noJobs')}</p>
        : <div className="jobs-grid">{deEstaPestana.map(job => <JobCard key={job.jobName} job={job} />)}</div>}
    </>
  );
}

export function UsuariosSubTab() {
  return (
    <>
      <div className="jobs-grid">
        <UserSourceCard />
        <PadronCard />
        <AdSyncCard />
      </div>
      <CorridasDe pestana="usuarios" />
    </>
  );
}

export function OusSubTab() {
  return (
    <>
      <div className="jobs-grid">
        <OuSourceCard />
      </div>
      <CorridasDe pestana="ous" />
    </>
  );
}

export function DominiosSubTab() {
  return (
    <>
      <div className="jobs-grid">
        <DomainsCard />
      </div>
      <CorridasDe pestana="dominios" />
    </>
  );
}

/** Sólo configuración: los centinelas no tienen corrida propia (ver `JOBS_POR_PESTANA`). */
export function CentinelasSubTab() {
  return (
    <div className="jobs-grid">
      <SentinelsCard />
    </div>
  );
}

/**
 * Sólo configuración, como Centinelas. 🔴 Pestaña propia y NO junto a «Dominios (Excel)», donde iba
 * antes: esa tarjeta edita `CorporateDomains`, pero la regla de staff de «A nombre de» usa
 * `SsoEmailDomains`. Lado a lado, invitaban a creer que esos dominios deciden quién es staff aquí
 * (decisión del tech lead, 2026-10-27).
 */
export function ANombreDeSubTab() {
  return (
    <div className="jobs-grid">
      <DelegationCard />
    </div>
  );
}
